import type { Workspace, FileInfo } from "@cloudflare/shell";
import type { JSONValue } from "ai";
import type { InitialFiles } from "just-bash";
import { Bash } from "just-bash";
import { tool } from "ai";
import { z } from "zod";

// ── WorkspaceLike ─────────────────────────────────────────────────
//
// Minimum workspace surface that Think's internals rely on. Covers the
// methods called by `createWorkspaceTools()` below plus the handful
// Think itself uses (`readFile`/`writeFile`/`readDir`/`rm` in
// `think.ts`). The read tool also needs `readFileBytes` so image/PDF
// reads can be rehydrated as AI SDK multimodal content. A concrete
// `Workspace` from `@cloudflare/shell` satisfies this; so do custom
// implementations like the `SharedWorkspace` proxy in `examples/assistant`
// that forwards to a parent DO.
//
// Consumers who reach for the fuller filesystem API (e.g.
// `createWorkspaceStateBackend` for codemode's `state.*` in a sandbox)
// still need a concrete `Workspace`.
//
// @experimental The API surface may change before stabilizing.

export type WorkspaceLike = Pick<
  Workspace,
  | "readFile"
  | "readFileBytes"
  | "writeFile"
  | "readDir"
  | "rm"
  | "glob"
  | "mkdir"
  | "stat"
>;

// ── Operations interfaces ─────────────────────────────────────────
// Abstractions over file I/O so the same tools can work against
// Workspace, a local filesystem, or anything else.

export interface ReadOperations {
  readFile(path: string): Promise<string | null>;
  readFileBytes(path: string): Promise<Uint8Array | null>;
  stat(path: string): Promise<FileInfo | null> | FileInfo | null;
}

export interface WriteOperations {
  writeFile(path: string, content: string): Promise<void>;
  mkdir(path: string, opts?: { recursive?: boolean }): Promise<void> | void;
}

export interface EditOperations {
  readFile(path: string): Promise<string | null>;
  writeFile(path: string, content: string): Promise<void>;
}

export interface ListOperations {
  readDir(
    dir: string,
    opts?: { limit?: number; offset?: number }
  ): Promise<FileInfo[]> | FileInfo[];
}

export interface FindOperations {
  glob(pattern: string): Promise<FileInfo[]> | FileInfo[];
}

export interface DeleteOperations {
  rm(
    path: string,
    opts?: { recursive?: boolean; force?: boolean }
  ): Promise<void>;
}

export interface GrepOperations {
  glob(pattern: string): Promise<FileInfo[]> | FileInfo[];
  readFile(path: string): Promise<string | null>;
}

export interface BashOperations {
  readDir(
    dir: string,
    opts?: { limit?: number; offset?: number }
  ): Promise<FileInfo[]> | FileInfo[];
  readFileBytes(path: string): Promise<Uint8Array | null>;
  writeFile(path: string, content: string): Promise<void>;
  writeFileBytes?: (path: string, content: Uint8Array) => Promise<void>;
  mkdir(path: string, opts?: { recursive?: boolean }): Promise<void> | void;
  rm(
    path: string,
    opts?: { recursive?: boolean; force?: boolean }
  ): Promise<void>;
}

// ── Workspace-backed operation factories ──────────────────────────

function workspaceReadOps(ws: WorkspaceLike): ReadOperations {
  return {
    readFile: (path) => ws.readFile(path),
    readFileBytes: (path) => ws.readFileBytes(path),
    stat: (path) => ws.stat(path)
  };
}

function workspaceWriteOps(ws: WorkspaceLike): WriteOperations {
  return {
    writeFile: (path, content) => ws.writeFile(path, content),
    mkdir: (path, opts) => ws.mkdir(path, opts)
  };
}

function workspaceEditOps(ws: WorkspaceLike): EditOperations {
  return {
    readFile: (path) => ws.readFile(path),
    writeFile: (path, content) => ws.writeFile(path, content)
  };
}

function workspaceListOps(ws: WorkspaceLike): ListOperations {
  return {
    readDir: (dir, opts) => ws.readDir(dir, opts)
  };
}

function workspaceFindOps(ws: WorkspaceLike): FindOperations {
  return {
    glob: (pattern) => ws.glob(pattern)
  };
}

function workspaceDeleteOps(ws: WorkspaceLike): DeleteOperations {
  return {
    rm: (path, opts) => ws.rm(path, opts)
  };
}

function workspaceGrepOps(ws: WorkspaceLike): GrepOperations {
  return {
    glob: (pattern) => ws.glob(pattern),
    readFile: (path) => ws.readFile(path)
  };
}

function workspaceBashOps(ws: WorkspaceLike): BashOperations {
  const maybeBytesWriter = ws as WorkspaceLike & {
    writeFileBytes?: (path: string, content: Uint8Array) => Promise<void>;
  };
  return {
    readDir: (dir, opts) => ws.readDir(dir, opts),
    readFileBytes: (path) => ws.readFileBytes(path),
    writeFile: (path, content) => ws.writeFile(path, content),
    writeFileBytes: maybeBytesWriter.writeFileBytes
      ? (path, content) => maybeBytesWriter.writeFileBytes!(path, content)
      : undefined,
    mkdir: (path, opts) => ws.mkdir(path, opts),
    rm: (path, opts) => ws.rm(path, opts)
  };
}

export interface WorkspaceToolsOptions {
  /**
   * Include the default workspace Bash tool. Enabled by default; pass `false`
   * to omit it, or an options object to tune execution limits.
   */
  bash?: boolean | Omit<BashToolOptions, "ops">;
}

export type WorkspaceTools = {
  read: ReturnType<typeof createReadTool>;
  write: ReturnType<typeof createWriteTool>;
  edit: ReturnType<typeof createEditTool>;
  list: ReturnType<typeof createListTool>;
  find: ReturnType<typeof createFindTool>;
  grep: ReturnType<typeof createGrepTool>;
  delete: ReturnType<typeof createDeleteTool>;
  bash?: ReturnType<typeof createBashTool>;
};

/**
 * Create a complete set of AI SDK tools backed by a workspace.
 *
 * Accepts either a concrete `Workspace` from `@cloudflare/shell` or any
 * `WorkspaceLike` implementation — e.g. a proxy that forwards calls to a
 * shared workspace owned by a parent DO.
 *
 * ```ts
 * import { Workspace } from "@cloudflare/shell";
 * import { createWorkspaceTools } from "@cloudflare/think";
 *
 * class MyAgent extends Agent<Env> {
 *   workspace = new Workspace({ sql: this.ctx.storage.sql, name: () => this.name });
 *
 *   async onChatMessage() {
 *     const tools = createWorkspaceTools(this.workspace);
 *     const result = streamText({ model, tools, messages });
 *     return result.toUIMessageStreamResponse();
 *   }
 * }
 * ```
 */
export function createWorkspaceTools(
  workspace: WorkspaceLike,
  options: WorkspaceToolsOptions = {}
): WorkspaceTools {
  const tools: WorkspaceTools = {
    read: createReadTool({ ops: workspaceReadOps(workspace) }),
    write: createWriteTool({ ops: workspaceWriteOps(workspace) }),
    edit: createEditTool({ ops: workspaceEditOps(workspace) }),
    list: createListTool({ ops: workspaceListOps(workspace) }),
    find: createFindTool({ ops: workspaceFindOps(workspace) }),
    grep: createGrepTool({ ops: workspaceGrepOps(workspace) }),
    delete: createDeleteTool({ ops: workspaceDeleteOps(workspace) })
  };

  if (options.bash !== false) {
    tools.bash = createBashTool({
      ...(typeof options.bash === "object" ? options.bash : {}),
      ops: workspaceBashOps(workspace)
    });
  }

  return tools;
}

// ── Read ────────────────────────────────────────────────────────────

const MAX_LINES = 2000;
const MAX_LINE_LENGTH = 2000;
const MAX_MODEL_FILE_BYTES = 3.5 * 1024 * 1024;

type TextReadToolOutput = {
  path: string;
  content: string;
  totalLines: number;
  fromLine?: number;
  toLine?: number;
};

type ImageReadToolOutput = {
  kind: "image";
  path: string;
  name: string;
  mediaType: string;
  sizeBytes: number;
};

type FileReadToolOutput = {
  kind: "file";
  path: string;
  name: string;
  mediaType: string;
  sizeBytes: number;
};

type BinaryReadToolOutput = {
  kind: "binary";
  path: string;
  name: string;
  mediaType: string;
  sizeBytes: number;
  unsupported: true;
};

type ReadToolError = { error: string };

type ReadToolOutput =
  | TextReadToolOutput
  | ImageReadToolOutput
  | FileReadToolOutput
  | BinaryReadToolOutput
  | ReadToolError;

type ReadToolInput = {
  path: string;
  offset?: number;
  limit?: number;
};

export interface ReadToolOptions {
  ops: ReadOperations;
}

export function createReadTool(options: ReadToolOptions) {
  const { ops } = options;

  return tool({
    description:
      "Read a workspace file. Text files return line-numbered content. " +
      "Images and PDFs are passed to capable models as file content. " +
      "Use offset and limit for large text files. Returns null if the file does not exist.",
    inputSchema: z.object({
      path: z.string().describe("Absolute path to the file"),
      offset: z
        .number()
        .int()
        .min(1)
        .optional()
        .describe("1-indexed line number to start reading from"),
      limit: z
        .number()
        .int()
        .min(1)
        .optional()
        .describe("Number of lines to read")
    }),
    execute: async ({
      path,
      offset,
      limit
    }: ReadToolInput): Promise<ReadToolOutput> => {
      const stat = await ops.stat(path);
      if (!stat) {
        return { error: `File not found: ${path}` };
      }
      if (stat.type === "directory") {
        return { error: `${path} is a directory, not a file` };
      }

      const mediaType = await detectWorkspaceMediaType({ ops, path, stat });

      if (mediaType.startsWith("image/")) {
        return {
          kind: "image",
          path,
          name: stat.name,
          mediaType,
          sizeBytes: stat.size
        };
      }

      if (mediaType === "application/pdf") {
        return {
          kind: "file",
          path,
          name: stat.name,
          mediaType,
          sizeBytes: stat.size
        };
      }

      if (!isTextMediaType(mediaType)) {
        return {
          kind: "binary",
          path,
          name: stat.name,
          mediaType,
          sizeBytes: stat.size,
          unsupported: true
        };
      }

      return readTextWithLineNumbers({ ops, path, offset, limit });
    },
    toModelOutput: async ({
      input,
      output
    }: {
      input: unknown;
      output: unknown;
    }) => {
      const replayOutput: unknown = output;

      if (!isRecord(replayOutput)) {
        return { type: "text", value: String(replayOutput) };
      }

      if (typeof replayOutput.error === "string") {
        return { type: "error-text", value: replayOutput.error };
      }

      if (typeof replayOutput.content === "string") {
        return { type: "text", value: replayOutput.content };
      }

      if (replayOutput.kind === "binary") {
        return {
          type: "json",
          value: toJSONValue(replayOutput)
        };
      }

      if (!isModelFileReadOutput(replayOutput)) {
        return {
          type: "json",
          value: toJSONValue(replayOutput)
        };
      }

      if (!isReadToolInput(input)) {
        return {
          type: "json",
          value: toJSONValue(replayOutput)
        };
      }

      const bytes = await ops.readFileBytes(input.path);
      if (bytes === null) {
        return {
          type: "error-text",
          value: `Could not read file bytes: ${input.path}`
        };
      }
      if (bytes.byteLength > MAX_MODEL_FILE_BYTES) {
        return {
          type: "error-text",
          value:
            `Read ${replayOutput.path} (${replayOutput.mediaType}, ${formatSize(bytes.byteLength)}), ` +
            `but it exceeds the ${formatSize(MAX_MODEL_FILE_BYTES)} inline model output limit.`
        };
      }

      const data = uint8ArrayToBase64(bytes);
      const note = `Read ${replayOutput.path} (${replayOutput.mediaType}, ${formatSize(bytes.byteLength)}).`;

      if (replayOutput.kind === "image") {
        return {
          type: "content",
          value: [
            { type: "text", text: note },
            { type: "image-data", data, mediaType: replayOutput.mediaType }
          ]
        };
      }

      return {
        type: "content",
        value: [
          { type: "text", text: note },
          {
            type: "file-data",
            data,
            mediaType: replayOutput.mediaType,
            filename: replayOutput.name
          }
        ]
      };
    }
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isModelFileReadOutput(
  value: Record<string, unknown>
): value is ImageReadToolOutput | FileReadToolOutput {
  return (
    (value.kind === "image" || value.kind === "file") &&
    typeof value.path === "string" &&
    typeof value.name === "string" &&
    typeof value.mediaType === "string"
  );
}

function isReadToolInput(value: unknown): value is ReadToolInput {
  return isRecord(value) && typeof value.path === "string";
}

function toJSONValue(value: unknown): JSONValue {
  try {
    const json = JSON.stringify(value);
    return json === undefined ? null : (JSON.parse(json) as JSONValue);
  } catch {
    return String(value);
  }
}

function readTextWithLineNumbers({
  ops,
  path,
  offset,
  limit
}: {
  ops: ReadOperations;
  path: string;
  offset?: number;
  limit?: number;
}): Promise<TextReadToolOutput | ReadToolError> {
  return Promise.resolve(ops.readFile(path)).then((content) => {
    if (content === null) {
      return { error: `Could not read file: ${path}` };
    }

    const allLines = content.split("\n");
    const totalLines = allLines.length;

    // Apply offset/limit
    const startLine = offset ? offset - 1 : 0;
    const endLine = limit ? startLine + limit : allLines.length;
    const lines = allLines.slice(startLine, endLine);

    // Format with line numbers, truncate long lines
    const numbered = lines.map((line, i) => {
      const lineNum = startLine + i + 1;
      const truncated =
        line.length > MAX_LINE_LENGTH
          ? line.slice(0, MAX_LINE_LENGTH) + "... (truncated)"
          : line;
      return `${lineNum}\t${truncated}`;
    });

    // Truncate if too many lines
    let output: string;
    if (numbered.length > MAX_LINES) {
      output =
        numbered.slice(0, MAX_LINES).join("\n") +
        `\n... (${numbered.length - MAX_LINES} more lines truncated)`;
    } else {
      output = numbered.join("\n");
    }

    const result: TextReadToolOutput = {
      path,
      content: output,
      totalLines
    };

    if (offset || limit) {
      result.fromLine = startLine + 1;
      result.toLine = Math.min(endLine, totalLines);
    }

    return result;
  });
}

async function detectWorkspaceMediaType({
  ops,
  path,
  stat
}: {
  ops: ReadOperations;
  path: string;
  stat: FileInfo;
}): Promise<string> {
  const statMime = normalizeMediaType(stat.mimeType);
  if (statMime && !isGenericMediaType(statMime)) {
    return statMime;
  }

  const bytes = await ops.readFileBytes(path);
  if (bytes === null) {
    return statMime || "application/octet-stream";
  }

  const sniffed = sniffMediaType(bytes);
  if (sniffed) {
    return sniffed;
  }

  return looksLikeText(bytes) ? "text/plain" : "application/octet-stream";
}

function normalizeMediaType(mediaType: string | undefined): string | null {
  const normalized = mediaType?.split(";")[0]?.trim().toLowerCase();
  return normalized || null;
}

function isGenericMediaType(mediaType: string): boolean {
  return (
    mediaType === "application/octet-stream" ||
    mediaType === "binary/octet-stream"
  );
}

function isTextMediaType(mediaType: string): boolean {
  return (
    mediaType.startsWith("text/") ||
    mediaType === "application/json" ||
    mediaType === "application/javascript" ||
    mediaType === "application/typescript" ||
    mediaType === "application/xml" ||
    mediaType === "application/x-javascript" ||
    mediaType.endsWith("+json") ||
    mediaType.endsWith("+xml")
  );
}

function sniffMediaType(bytes: Uint8Array): string | null {
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    return "image/png";
  }
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) {
    return "image/jpeg";
  }
  if (startsWithAscii(bytes, "GIF87a") || startsWithAscii(bytes, "GIF89a")) {
    return "image/gif";
  }
  if (startsWithAscii(bytes, "RIFF") && asciiAt(bytes, 8, 12) === "WEBP") {
    return "image/webp";
  }
  if (startsWithAscii(bytes, "%PDF-")) {
    return "application/pdf";
  }
  if (looksLikeSvg(bytes)) {
    return "image/svg+xml";
  }
  return null;
}

function startsWith(bytes: Uint8Array, prefix: number[]): boolean {
  if (bytes.length < prefix.length) return false;
  return prefix.every((byte, index) => bytes[index] === byte);
}

function startsWithAscii(bytes: Uint8Array, prefix: string): boolean {
  return asciiAt(bytes, 0, prefix.length) === prefix;
}

function asciiAt(bytes: Uint8Array, start: number, end: number): string {
  return String.fromCharCode(...bytes.subarray(start, end));
}

function looksLikeSvg(bytes: Uint8Array): boolean {
  const prefix = new TextDecoder()
    .decode(bytes.subarray(0, Math.min(bytes.length, 512)))
    .trimStart()
    .toLowerCase();
  return prefix.startsWith("<svg") || prefix.includes("<svg");
}

function looksLikeText(bytes: Uint8Array): boolean {
  if (bytes.length === 0) return true;
  if (bytes.includes(0)) return false;

  const text = new TextDecoder().decode(bytes.subarray(0, 4096));
  if (text.length === 0) return true;

  let replacementChars = 0;
  for (const char of text) {
    if (char === "\uFFFD") {
      replacementChars++;
    }
  }
  return replacementChars / text.length < 0.01;
}

function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

// ── Write ───────────────────────────────────────────────────────────

export interface WriteToolOptions {
  ops: WriteOperations;
}

export function createWriteTool(options: WriteToolOptions) {
  const { ops } = options;

  return tool({
    description:
      "Write content to a file. Creates the file if it does not exist, " +
      "overwrites if it does. Parent directories are created automatically.",
    inputSchema: z.object({
      path: z.string().describe("Absolute path to the file"),
      content: z.string().describe("Content to write to the file")
    }),
    execute: async ({ path, content }) => {
      // Ensure parent directory exists
      const parent = path.replace(/\/[^/]+$/, "");
      if (parent && parent !== "/") {
        await ops.mkdir(parent, { recursive: true });
      }

      await ops.writeFile(path, content);

      const lines = content.split("\n").length;
      return {
        path,
        bytesWritten: new TextEncoder().encode(content).byteLength,
        lines
      };
    }
  });
}

// ── Edit ────────────────────────────────────────────────────────────

export interface EditToolOptions {
  ops: EditOperations;
}

export function createEditTool(options: EditToolOptions) {
  const { ops } = options;

  return tool({
    description:
      "Make a targeted edit to a file by replacing an exact string match. " +
      "Provide the old_string to find and new_string to replace it with. " +
      "The old_string must match exactly (including whitespace and indentation). " +
      "Use an empty old_string with new_string to create a new file.",
    inputSchema: z.object({
      path: z.string().describe("Absolute path to the file"),
      old_string: z
        .string()
        .describe(
          "Exact text to find and replace. Empty string to create a new file."
        ),
      new_string: z.string().describe("Replacement text")
    }),
    execute: async ({ path, old_string, new_string }) => {
      // Create new file
      if (old_string === "") {
        const existing = await ops.readFile(path);
        if (existing !== null) {
          return {
            error:
              "File already exists. Provide old_string to edit, or use the write tool to overwrite."
          };
        }
        await ops.writeFile(path, new_string);
        return {
          path,
          created: true,
          lines: new_string.split("\n").length
        };
      }

      // Edit existing file
      const content = await ops.readFile(path);
      if (content === null) {
        return { error: `File not found: ${path}` };
      }

      // Count occurrences
      const occurrences = countOccurrences(content, old_string);
      if (occurrences === 0) {
        // Try fuzzy match — normalize whitespace and look again
        const fuzzyResult = fuzzyReplace(content, old_string, new_string);
        if (fuzzyResult === "ambiguous") {
          return {
            error:
              "old_string matches multiple locations after whitespace normalization. " +
              "Include more surrounding context to make the match unique."
          };
        }
        if (fuzzyResult !== null) {
          await ops.writeFile(path, fuzzyResult);
          return {
            path,
            replaced: true,
            fuzzyMatch: true,
            lines: fuzzyResult.split("\n").length
          };
        }

        return {
          error:
            "old_string not found in file. Make sure it matches exactly, " +
            "including whitespace and indentation. Read the file first to verify."
        };
      }

      if (occurrences > 1) {
        return {
          error:
            `old_string appears ${occurrences} times in the file. ` +
            "Include more surrounding context to make the match unique."
        };
      }

      const newContent = content.replace(old_string, new_string);
      await ops.writeFile(path, newContent);

      return {
        path,
        replaced: true,
        lines: newContent.split("\n").length
      };
    }
  });
}

function countOccurrences(text: string, search: string): number {
  let count = 0;
  let pos = 0;
  while (true) {
    const idx = text.indexOf(search, pos);
    if (idx === -1) break;
    count++;
    pos = idx + 1;
  }
  return count;
}

/**
 * Fuzzy replacement: normalize whitespace in both the file content
 * and the search string, find the match, then replace the corresponding
 * region in the original content.
 */
function fuzzyReplace(
  content: string,
  oldStr: string,
  newStr: string
): string | "ambiguous" | null {
  const normalizedContent = normalizeWhitespace(content);
  const normalizedSearch = normalizeWhitespace(oldStr);

  if (!normalizedSearch) return null;

  const idx = normalizedContent.indexOf(normalizedSearch);
  if (idx === -1) return null;

  // Check for multiple fuzzy matches
  const secondIdx = normalizedContent.indexOf(
    normalizedSearch,
    idx + normalizedSearch.length
  );
  if (secondIdx !== -1) return "ambiguous";

  // Map the normalized index back to the original content.
  // Walk both strings in parallel to find the original start/end.
  const originalStart = mapToOriginal(content, idx);
  const originalEnd = mapToOriginal(content, idx + normalizedSearch.length);

  return content.slice(0, originalStart) + newStr + content.slice(originalEnd);
}

function normalizeWhitespace(s: string): string {
  return s.replace(/[ \t]+/g, " ").replace(/\r\n/g, "\n");
}

/**
 * Map a position in the normalized string back to the original string.
 * Walks both strings char-by-char, skipping extra whitespace in the original.
 */
function mapToOriginal(original: string, normalizedPos: number): number {
  let ni = 0;
  let oi = 0;

  while (ni < normalizedPos && oi < original.length) {
    const oc = original[oi];
    if (oc === "\r" && original[oi + 1] === "\n") {
      // \r\n in original maps to \n in normalized
      oi += 2;
      ni += 1;
    } else if (oc === " " || oc === "\t") {
      // Consume a run of spaces/tabs in original → single space in normalized
      oi++;
      while (
        oi < original.length &&
        (original[oi] === " " || original[oi] === "\t")
      ) {
        oi++;
      }
      ni++;
    } else {
      oi++;
      ni++;
    }
  }

  return oi;
}

// ── List ────────────────────────────────────────────────────────────

export interface ListToolOptions {
  ops: ListOperations;
}

export function createListTool(options: ListToolOptions) {
  const { ops } = options;

  return tool({
    description:
      "List files and directories in a given path. " +
      "Returns names, types, and sizes for each entry.",
    inputSchema: z.object({
      path: z
        .string()
        .default("/")
        .describe("Absolute path to the directory to list"),
      limit: z
        .number()
        .int()
        .min(1)
        .max(1000)
        .optional()
        .describe("Maximum number of entries to return (default: 200)"),
      offset: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe("Number of entries to skip (for pagination)")
    }),
    execute: async ({ path, limit, offset }) => {
      const maxEntries = limit ?? 200;
      const entries = await ops.readDir(path, {
        limit: maxEntries,
        offset: offset ?? 0
      });

      const formatted = entries.map((entry) => {
        const suffix = entry.type === "directory" ? "/" : "";
        const sizeStr =
          entry.type === "file" ? ` (${formatSize(entry.size)})` : "";
        return `${entry.name}${suffix}${sizeStr}`;
      });

      return {
        path,
        count: entries.length,
        entries: formatted
      };
    }
  });
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Find ────────────────────────────────────────────────────────────

export interface FindToolOptions {
  ops: FindOperations;
}

export function createFindTool(options: FindToolOptions) {
  const { ops } = options;

  return tool({
    description:
      "Find files matching a glob pattern. " +
      "Supports standard glob syntax: * matches any file, ** matches directories recursively, " +
      "? matches a single character. Returns matching file paths with types and sizes.",
    inputSchema: z.object({
      pattern: z
        .string()
        .describe(
          'Glob pattern to match (e.g. "**/*.ts", "src/**/*.test.ts", "*.md")'
        )
    }),
    execute: async ({ pattern }) => {
      const matches = await ops.glob(pattern);

      const MAX_RESULTS = 200;
      const truncated = matches.length > MAX_RESULTS;
      const results = matches.slice(0, MAX_RESULTS);

      const formatted = results.map((entry) => {
        const suffix = entry.type === "directory" ? "/" : "";
        return `${entry.path}${suffix}`;
      });

      const result: Record<string, unknown> = {
        pattern,
        count: matches.length,
        files: formatted
      };

      if (truncated) {
        result.truncated = true;
        result.showing = MAX_RESULTS;
      }

      return result;
    }
  });
}

// ── Grep ────────────────────────────────────────────────────────────

const MAX_MATCHES = 200;
const MAX_FILE_SIZE = 1_048_576; // 1 MB — skip files larger than this in grep

export interface GrepToolOptions {
  ops: GrepOperations;
}

export function createGrepTool(options: GrepToolOptions) {
  const { ops } = options;

  return tool({
    description:
      "Search file contents using a regular expression or fixed string. " +
      "Returns matching lines with file paths and line numbers. " +
      "Searches all files matching the include glob, or all files if not specified.",
    inputSchema: z.object({
      query: z.string().describe("Search pattern (regex or fixed string)"),
      include: z
        .string()
        .optional()
        .describe(
          'Glob pattern to filter files (e.g. "**/*.ts"). Defaults to "**/*"'
        ),
      fixedString: z
        .boolean()
        .optional()
        .describe("If true, treat query as a literal string instead of regex"),
      caseSensitive: z
        .boolean()
        .optional()
        .describe("If true, search is case-sensitive (default: false)"),
      contextLines: z
        .number()
        .int()
        .min(0)
        .max(10)
        .optional()
        .describe("Number of context lines around each match (default: 0)")
    }),
    execute: async ({
      query,
      include,
      fixedString,
      caseSensitive,
      contextLines
    }) => {
      const pattern = include ?? "**/*";
      const allFiles = await ops.glob(pattern);
      const files = allFiles.filter((f: { type: string }) => f.type === "file");

      let regex: RegExp;
      try {
        const escaped = fixedString ? escapeRegex(query) : query;
        regex = new RegExp(escaped, caseSensitive ? "g" : "gi");
      } catch {
        return { error: `Invalid regex: ${query}` };
      }

      const ctx = contextLines ?? 0;
      const matches: Array<{
        file: string;
        line: number;
        text: string;
        context?: string[];
      }> = [];
      let totalMatches = 0;
      let filesSearched = 0;
      let filesWithMatches = 0;

      let filesSkipped = 0;

      for (const file of files) {
        if (totalMatches >= MAX_MATCHES) break;

        // Skip files larger than 1 MB to avoid memory blowup
        if (file.size > MAX_FILE_SIZE) {
          filesSkipped++;
          continue;
        }

        const content = await ops.readFile(file.path);
        if (content === null) continue;
        filesSearched++;

        const lines = content.split("\n");
        let fileHasMatch = false;

        for (let i = 0; i < lines.length; i++) {
          if (totalMatches >= MAX_MATCHES) break;

          regex.lastIndex = 0;
          if (regex.test(lines[i])) {
            if (!fileHasMatch) {
              fileHasMatch = true;
              filesWithMatches++;
            }
            totalMatches++;

            const match: {
              file: string;
              line: number;
              text: string;
              context?: string[];
            } = {
              file: file.path,
              line: i + 1,
              text: lines[i]
            };

            if (ctx > 0) {
              const start = Math.max(0, i - ctx);
              const end = Math.min(lines.length, i + ctx + 1);
              match.context = lines.slice(start, end).map((l, j) => {
                const lineNum = start + j + 1;
                const marker = lineNum === i + 1 ? ">" : " ";
                return `${marker} ${lineNum}\t${l}`;
              });
            }

            matches.push(match);
          }
        }
      }

      const result: Record<string, unknown> = {
        query,
        filesSearched,
        filesWithMatches,
        totalMatches,
        matches: matches.map((m) => {
          if (m.context) {
            return {
              file: m.file,
              line: m.line,
              context: m.context.join("\n")
            };
          }
          return `${m.file}:${m.line}: ${m.text}`;
        })
      };

      if (totalMatches >= MAX_MATCHES) {
        result.truncated = true;
      }
      if (filesSkipped > 0) {
        result.filesSkipped = filesSkipped;
        result.note = `${filesSkipped} file(s) skipped (larger than 1 MB)`;
      }

      return result;
    }
  });
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// ── Bash ─────────────────────────────────────────────────────────────

const DEFAULT_BASH_TIMEOUT_MS = 30_000;
const DEFAULT_BASH_MAX_WORKSPACE_FILES = 1_000;
const DEFAULT_BASH_MAX_WORKSPACE_FILE_BYTES = 1_000_000;
const DEFAULT_BASH_MAX_OUTPUT_BYTES = 64_000;
const BASH_READDIR_PAGE_SIZE = 1_000;
// Synthetic paths the bash sandbox creates for itself (shell builtins under
// /bin and /usr/bin, pseudo-filesystems, scratch space). New files here must
// never be persisted to the workspace — only pre-existing workspace files
// under these roots keep syncing.
const BASH_EXCLUDED_SYNC_ROOTS = ["/bin", "/usr", "/dev", "/proc", "/sys"];

type BashToolInput = {
  script: string;
  cwd?: string;
};

type BashChangedFiles = {
  created: string[];
  updated: string[];
  deleted: string[];
  directoriesCreated: string[];
  directoriesDeleted: string[];
};

export interface BashToolOptions {
  ops: BashOperations;
  timeout?: number;
  network?: boolean;
  maxWorkspaceFiles?: number;
  maxWorkspaceFileBytes?: number;
  maxOutputBytes?: number;
}

export function createBashTool(options: BashToolOptions) {
  return tool({
    description:
      "Run a Bash script against the workspace. Use for shell-style workflows " +
      "that combine multiple file operations. The script runs in a sandboxed " +
      "virtual filesystem with the workspace mounted at `/` (also the default " +
      "working directory) — there is no `/workspace` or `/home`; use absolute " +
      "paths like `/notes.txt`. Changed files are written back to the workspace.",
    inputSchema: z.object({
      script: z.string().describe("Bash script to run"),
      cwd: z
        .string()
        .optional()
        .describe(
          "Working directory for the script. Defaults to / (the workspace root)"
        )
    }),
    execute: async ({ script, cwd }: BashToolInput) => {
      const timeout = options.timeout ?? DEFAULT_BASH_TIMEOUT_MS;
      const maxOutputBytes =
        options.maxOutputBytes ?? DEFAULT_BASH_MAX_OUTPUT_BYTES;
      const snapshot = await snapshotWorkspaceForBash(options);
      const bash = new Bash({
        files: snapshot.files,
        cwd: normalizeWorkspacePath(cwd ?? "/"),
        defenseInDepth: true,
        network: options.network ? {} : undefined
      });

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeout);
      let result:
        | {
            stdout: string;
            stderr: string;
            exitCode: number;
          }
        | undefined;
      try {
        for (const directory of snapshot.directories) {
          await bash.fs.mkdir(directory, { recursive: true }).catch(() => {});
        }

        try {
          result = await bash.exec(script, {
            cwd: normalizeWorkspacePath(cwd ?? "/"),
            signal: controller.signal,
            rawScript: true
          });
        } catch (error) {
          result = {
            stdout: "",
            stderr: errorMessage(error),
            exitCode: controller.signal.aborted ? 124 : 1
          };
        }

        const sync = await syncBashFilesToWorkspace({
          ops: options.ops,
          bash,
          initialFiles: snapshot.initialFiles,
          initialDirectories: snapshot.initialDirectories,
          protectedPaths: snapshot.protectedPaths
        });

        return {
          stdout: truncateToolOutput(result.stdout, maxOutputBytes),
          stderr: truncateToolOutput(result.stderr, maxOutputBytes),
          exitCode: result.exitCode,
          changedFiles: sync.changedFiles,
          ...(snapshot.skippedFiles.length > 0
            ? { skippedFiles: snapshot.skippedFiles }
            : {}),
          ...(sync.errors.length > 0 ? { errors: sync.errors } : {})
        };
      } finally {
        clearTimeout(timer);
      }
    }
  });
}

async function snapshotWorkspaceForBash(options: BashToolOptions): Promise<{
  files: InitialFiles;
  initialFiles: Map<string, Uint8Array>;
  initialDirectories: Set<string>;
  protectedPaths: Set<string>;
  directories: string[];
  skippedFiles: string[];
}> {
  const maxFiles =
    options.maxWorkspaceFiles ?? DEFAULT_BASH_MAX_WORKSPACE_FILES;
  const maxFileBytes =
    options.maxWorkspaceFileBytes ?? DEFAULT_BASH_MAX_WORKSPACE_FILE_BYTES;
  const files: InitialFiles = {};
  const initialFiles = new Map<string, Uint8Array>();
  const initialDirectories = new Set<string>(["/"]);
  const protectedPaths = new Set<string>();
  const skippedFiles: string[] = [];
  const pending = ["/"];

  while (pending.length > 0) {
    const dir = pending.shift()!;
    const entries = await readAllBashDirEntries(options.ops, dir);
    for (const entry of entries) {
      const path = normalizeWorkspacePath(entry.path);
      if (entry.type === "directory") {
        initialDirectories.add(path);
        pending.push(path);
        continue;
      }
      if (entry.type !== "file") continue;
      if (initialFiles.size >= maxFiles || entry.size > maxFileBytes) {
        protectedPaths.add(path);
        skippedFiles.push(path);
        continue;
      }
      const bytes = await options.ops.readFileBytes(path);
      if (bytes === null) {
        protectedPaths.add(path);
        skippedFiles.push(path);
        continue;
      }
      files[path] = bytes;
      initialFiles.set(path, bytes);
    }
  }

  return {
    files,
    initialFiles,
    initialDirectories,
    protectedPaths,
    directories: [...initialDirectories].sort((a, b) => a.localeCompare(b)),
    skippedFiles
  };
}

async function readAllBashDirEntries(
  ops: BashOperations,
  dir: string
): Promise<FileInfo[]> {
  const entries: FileInfo[] = [];
  let offset = 0;

  while (true) {
    const page = await ops.readDir(dir, {
      limit: BASH_READDIR_PAGE_SIZE,
      offset
    });
    entries.push(...page);
    if (page.length !== BASH_READDIR_PAGE_SIZE) break;
    offset += page.length;
  }

  return entries;
}

async function syncBashFilesToWorkspace({
  ops,
  bash,
  initialFiles,
  initialDirectories,
  protectedPaths
}: {
  ops: BashOperations;
  bash: {
    fs: {
      getAllPaths(): string[];
      stat(path: string): Promise<{ isFile: boolean; isDirectory: boolean }>;
      readFileBuffer(path: string): Promise<Uint8Array>;
    };
  };
  initialFiles: Map<string, Uint8Array>;
  initialDirectories: Set<string>;
  protectedPaths: Set<string>;
}): Promise<{ changedFiles: BashChangedFiles; errors: string[] }> {
  const changedFiles: BashChangedFiles = {
    created: [],
    updated: [],
    deleted: [],
    directoriesCreated: [],
    directoriesDeleted: []
  };
  const errors: string[] = [];
  const finalFiles = new Map<string, Uint8Array>();
  const finalDirectories = new Set<string>(["/"]);

  for (const rawPath of bash.fs.getAllPaths()) {
    const path = normalizeWorkspacePath(rawPath);
    if (!shouldSyncBashPath(path, initialFiles, protectedPaths)) continue;
    const stat = await bash.fs.stat(path).catch(() => null);
    if (stat?.isDirectory) {
      finalDirectories.add(path);
      continue;
    }
    if (stat?.isFile) {
      finalFiles.set(path, await bash.fs.readFileBuffer(path));
    }
  }

  for (const path of [...finalDirectories].sort((a, b) => a.localeCompare(b))) {
    if (path === "/" || initialDirectories.has(path)) continue;
    if (hasProtectedDescendant(path, protectedPaths)) {
      errors.push(
        `Skipped creating directory ${path}: contains protected paths.`
      );
      continue;
    }
    const created = await Promise.resolve(ops.mkdir(path, { recursive: true }))
      .then(() => true)
      .catch((error: unknown) => {
        errors.push(
          `Failed to create directory ${path}: ${errorMessage(error)}`
        );
        return false;
      });
    if (!created) continue;
    changedFiles.directoriesCreated.push(path);
  }

  for (const [path, bytes] of finalFiles) {
    if (protectedPaths.has(path)) {
      errors.push(`Skipped writing protected workspace file: ${path}`);
      continue;
    }
    const existing = initialFiles.get(path);
    if (existing && bytesEqual(existing, bytes)) continue;
    const written = await writeWorkspaceBytes(ops, path, bytes, errors);
    if (!written) continue;
    if (existing) changedFiles.updated.push(path);
    else changedFiles.created.push(path);
  }

  for (const path of [...initialFiles.keys()].sort((a, b) =>
    b.localeCompare(a)
  )) {
    if (finalFiles.has(path) || protectedPaths.has(path)) continue;
    const deleted = await ops
      .rm(path, { force: true })
      .then(() => true)
      .catch((error: unknown) => {
        errors.push(`Failed to delete ${path}: ${errorMessage(error)}`);
        return false;
      });
    if (!deleted) continue;
    changedFiles.deleted.push(path);
  }

  for (const path of [...initialDirectories].sort((a, b) =>
    b.localeCompare(a)
  )) {
    if (path === "/" || finalDirectories.has(path)) continue;
    if (hasProtectedDescendant(path, protectedPaths)) {
      errors.push(`Skipped deleting protected workspace directory: ${path}`);
      continue;
    }
    const deleted = await ops
      .rm(path, { recursive: true, force: true })
      .then(() => true)
      .catch((error: unknown) => {
        errors.push(
          `Failed to delete directory ${path}: ${errorMessage(error)}`
        );
        return false;
      });
    if (!deleted) continue;
    changedFiles.directoriesDeleted.push(path);
  }

  changedFiles.created.sort();
  changedFiles.updated.sort();
  changedFiles.deleted.sort();
  changedFiles.directoriesCreated.sort();
  changedFiles.directoriesDeleted.sort();

  return { changedFiles, errors };
}

async function writeWorkspaceBytes(
  ops: BashOperations,
  path: string,
  bytes: Uint8Array,
  errors: string[]
): Promise<boolean> {
  const parent = parentDir(path);
  if (parent !== "/") {
    const parentCreated = await Promise.resolve(
      ops.mkdir(parent, { recursive: true })
    )
      .then(() => true)
      .catch((error: unknown) => {
        errors.push(
          `Failed to create parent directory ${parent}: ${errorMessage(error)}`
        );
        return false;
      });
    if (!parentCreated) return false;
  }

  if (ops.writeFileBytes) {
    return ops
      .writeFileBytes(path, bytes)
      .then(() => true)
      .catch((error: unknown) => {
        errors.push(`Failed to write ${path}: ${errorMessage(error)}`);
        return false;
      });
  }

  if (!looksLikeText(bytes)) {
    errors.push(
      `Could not persist binary file ${path}: workspace does not support writeFileBytes.`
    );
    return false;
  }

  const content = new TextDecoder().decode(bytes);
  return ops
    .writeFile(path, content)
    .then(() => true)
    .catch((error: unknown) => {
      errors.push(`Failed to write ${path}: ${errorMessage(error)}`);
      return false;
    });
}

function shouldSyncBashPath(
  path: string,
  initialFiles: Map<string, Uint8Array>,
  protectedPaths: Set<string>
): boolean {
  if (path === "/") return false;
  if (initialFiles.has(path)) return true;
  if (protectedPaths.has(path)) return true;
  if (path === "/tmp" || path.startsWith("/tmp/")) return false;
  return !BASH_EXCLUDED_SYNC_ROOTS.some(
    (root) => path === root || path.startsWith(`${root}/`)
  );
}

function hasProtectedDescendant(
  path: string,
  protectedPaths: Set<string>
): boolean {
  const prefix = path.endsWith("/") ? path : `${path}/`;
  for (const protectedPath of protectedPaths) {
    if (protectedPath.startsWith(prefix)) return true;
  }
  return false;
}

function normalizeWorkspacePath(path: string): string {
  const parts: string[] = [];
  const raw = path.startsWith("/") ? path : `/${path}`;
  for (const part of raw.split("/")) {
    if (!part || part === ".") continue;
    if (part === "..") parts.pop();
    else parts.push(part);
  }
  return `/${parts.join("/")}`;
}

function parentDir(path: string): string {
  const normalized = normalizeWorkspacePath(path);
  const index = normalized.lastIndexOf("/");
  return index <= 0 ? "/" : normalized.slice(0, index);
}

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.byteLength !== b.byteLength) return false;
  for (let i = 0; i < a.byteLength; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function truncateToolOutput(value: string, maxBytes: number): string {
  const bytes = new TextEncoder().encode(value);
  if (bytes.byteLength <= maxBytes) return value;
  const truncated = new TextDecoder().decode(bytes.slice(0, maxBytes));
  return `${truncated}\n... (${formatSize(bytes.byteLength - maxBytes)} truncated)`;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

// ── Delete ──────────────────────────────────────────────────────────

export interface DeleteToolOptions {
  ops: DeleteOperations;
}

export function createDeleteTool(options: DeleteToolOptions) {
  const { ops } = options;

  return tool({
    description:
      "Delete a file or directory. " +
      "Set recursive to true to remove non-empty directories.",
    inputSchema: z.object({
      path: z.string().describe("Absolute path to the file or directory"),
      recursive: z
        .boolean()
        .optional()
        .describe("If true, remove directories and their contents recursively")
    }),
    execute: async ({ path, recursive }) => {
      await ops.rm(path, { recursive, force: true });
      return { deleted: path };
    }
  });
}
